import type { HomeyAPI, ItemProps, User } from 'homey-api';

import { __DEV__ } from '../../lib/__dev__';
import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';
import { KeyedAsyncQueue } from '../KeyedAsyncQueue.ts';
import {
  OptimisticEntityUpdates,
  type OptimisticEntityMutationResult,
} from '../OptimisticEntityUpdates.ts';

export type UserProps = ItemProps<User>;

type UsersData = { [key: string]: User };
type UsersById = { [key: string]: UserProps };

// One field per update, so a failed request can't roll back a field that already saved.
export type UserPresenceUpdate = { present: boolean } | { asleep: boolean };

type UserOptimisticOperation = {
  type: 'updatePresence';
  presence: UserPresenceUpdate;
};

interface State {
  data: UsersData | null;
  byId: UsersById | null;
  isLoading: boolean;
  error: unknown;
}

export type UsersStoreState = State;

export class UsersStore extends BaseApiStore<State, User> {
  #optimisticUpdates = new OptimisticEntityUpdates<UserProps, UserOptimisticOperation>({
    applyOperation: UsersStore.#applyOptimisticOperation,
    reconcileMutationResult: UsersStore.#reconcileOptimisticMutationResult,
  });
  #persistenceQueue = new KeyedAsyncQueue<string>();

  constructor() {
    super('users');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ manager: api.users });

      if (abortController.signal.aborted) {
        return;
      }

      const baseRevision = this.#optimisticUpdates.captureBaseRevision();
      const usersPromise = api.users.getUsers({ $cache: cache });

      const [usersData] = await Promise.all([usersPromise]);

      if (abortController.signal.aborted) {
        return;
      }

      const users = Object.values(usersData);

      const data: UsersData = {};
      const byId: UsersById = {};

      for (const userInstance of users) {
        const userReference = userInstance.getProps();

        data[userReference.id] = userInstance;
        byId[userReference.id] = userReference;
      }

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: this.#optimisticUpdates.reconcileBaseSnapshot(baseRevision, byId),
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy(reason: string) {
    this.disconnectManager().catch(console.error);

    this.#optimisticUpdates.clear();
    this.#persistenceQueue.clear();
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.users.on('user.create', this.handleCreate);
    api.users.on('user.update', this.handleUpdate);
    api.users.on('user.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.users.off('user.create', this.handleCreate);
    api.users.off('user.update', this.handleUpdate);
    api.users.off('user.delete', this.handleDelete);
  }

  getInstanceFromCache(user: { id: string }) {
    return this.getCache()[user.id];
  }

  getCache(): Readonly<Record<string, User>> {
    const api = this.getApi();

    return api.users.getCachedItems('user');
  }

  handleCreate = (createdUser: User) => {
    this.DEBUG && console.info(`create:user:${createdUser.id}`);
    const state = this.get();
    const userInstance = this.getInstanceFromCache(createdUser);

    if (userInstance == null) {
      return;
    }

    const userReference = this.#optimisticUpdates.reconcileBaseEntity(
      createdUser.id,
      userInstance.getProps(),
    );
    const nextById = { ...state.byId };

    if (userReference == null) {
      delete nextById[createdUser.id];
    } else {
      nextById[createdUser.id] = userReference;
    }

    this.set({
      data: {
        ...state.data,
        [createdUser.id]: userInstance,
      },
      byId: nextById,
    });
  };

  handleUpdate = (updatedUser: User) => {
    this.DEBUG && console.info(`update:user:${updatedUser.id}`);

    this.addToQueue(updatedUser.id, updatedUser);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedUser] of Object.entries(updateQueue)) {
        const userInstance = this.getInstanceFromCache(queuedUser);

        if (state.data == null) {
          this.DEBUG && console.info(`update:user:missing-data`);
          continue;
        }

        if (userInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:user:unknown:${id}`);
          continue;
        }

        const userReference = this.#optimisticUpdates.reconcileBaseEntity(
          id,
          userInstance.getProps(),
        );

        if (userReference == null) {
          delete nextById[id];
        } else {
          nextById[id] = userReference;
        }

        updateCount++;
      }

      this.DEBUG && console.info(`update:user:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedUser: Pick<User, 'id'>) => {
    this.DEBUG && console.info(`delete:user:${deletedUser.id}`);
    const state = this.get();

    if (state.data == null) {
      __DEV__ && console.log(`delete:user:missing-data`);
      return;
    }

    const userInstance = state.data[deletedUser.id];

    if (userInstance == null) {
      return;
    }

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[userInstance.id];
    const optimisticUser = this.#optimisticUpdates.reconcileBaseEntity(userInstance.id, undefined);

    if (optimisticUser == null) {
      delete nextById[userInstance.id];
    } else {
      nextById[userInstance.id] = optimisticUser;
    }

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const baseRevision = this.#optimisticUpdates.captureBaseRevision();
    const cache = this.getCache();
    const users = Object.values(cache ?? {});
    const data: UsersData = {};
    const byId: UsersById = {};

    for (const userInstance of users) {
      const userReference = userInstance.getProps();
      data[userReference.id] = userInstance;
      byId[userReference.id] = userReference;
    }

    this.set({
      data: data,
      byId: this.#optimisticUpdates.reconcileBaseSnapshot(baseRevision, byId),
    });
  }

  async updatePresenceOptimistically({
    id,
    isMe,
    presence,
  }: {
    id: string;
    // The own presence has its own endpoints, which need fewer permissions.
    isMe: boolean;
    presence: UserPresenceUpdate;
  }): Promise<void> {
    const currentUser = this.get().byId?.[id];

    if (currentUser == null) {
      throw new Error(`Unknown user: ${id}`);
    }

    const api = this.getApi();
    const operation: UserOptimisticOperation = { type: 'updatePresence', presence };
    const { mutationId, entity: optimisticUser } = this.#optimisticUpdates.beginMutation(
      id,
      currentUser,
      operation,
    );

    this.#publishUser(id, optimisticUser);

    await this.#persistenceQueue.enqueue(id, async () => {
      this.#optimisticUpdates.startPersistence(id, mutationId);

      try {
        if ('present' in presence) {
          if (isMe) {
            await api.presence.setPresentMe({ value: presence.present });
          } else {
            await api.presence.setPresent({ id, value: presence.present });
          }
        } else {
          if (isMe) {
            await api.presence.setAsleepMe({ value: presence.asleep });
          } else {
            await api.presence.setAsleep({ id, value: presence.asleep });
          }
        }

        // The endpoints only return the new value, so the operation is applied to the base.
        this.#finishOptimisticMutation(id, mutationId, { status: 'success' });
      } catch (error) {
        this.#finishOptimisticMutation(id, mutationId, { status: 'failure' });
        throw error;
      }
    });
  }

  #finishOptimisticMutation(
    userId: string,
    mutationId: number,
    result: OptimisticEntityMutationResult<UserProps>,
  ): void {
    const finishResult = this.#optimisticUpdates.finishMutation(userId, mutationId, result);

    if (finishResult.status === 'ignored') {
      return;
    }

    this.#publishUser(userId, finishResult.entity);
  }

  #publishUser(userId: string, user: UserProps | undefined): void {
    const state = this.get();

    if (state.byId == null) {
      return;
    }

    const nextById = { ...state.byId };

    if (user == null) {
      delete nextById[userId];
    } else {
      nextById[userId] = user;
    }

    this.set({ byId: nextById });
  }

  static #applyOptimisticOperation(
    user: UserProps | undefined,
    operation: UserOptimisticOperation,
  ): UserProps | undefined {
    if (user == null) {
      return undefined;
    }

    return Object.assign({}, user, operation.presence);
  }

  static #reconcileOptimisticMutationResult(
    baseUser: UserProps | undefined,
    mutationUser: UserProps | undefined,
    operation: UserOptimisticOperation,
  ): UserProps | undefined {
    if (baseUser == null || mutationUser == null) {
      return undefined;
    }

    return Object.assign(
      {},
      baseUser,
      'present' in operation.presence ? { present: mutationUser.present } : {},
      'asleep' in operation.presence ? { asleep: mutationUser.asleep } : {},
    );
  }
}

export const usersStore = new UsersStore();
